import { useState, useEffect, useRef } from 'react'
import { clearHistory, getHistory, updateHistoryRecord } from '../utils/storage';
import Toast from '../components/Toast';
import RewriteModal from '../components/history/RewriteModal';
import { dispatchCreditUpdateEvent, startUseCredit } from '../utils/creditSystem';
import { isAuthenticated } from '../utils/auth';
import HistoryRecord from '../components/history/HistoryRecord';
import { apiRequest, getMyVoices } from '../utils/api';
import AvatarGenerationModal from '../components/voice/AvatarGenerationModal';

function History() {
    const [historyRecords, setHistoryRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [rewriteResult, setRewriteResult] = useState(null);
    const [myVoices, setMyVoices] = useState([]);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
    const [avatarGenerationStatus, setAvatarGenerationStatus] = useState(null);
    const [isAvatarGenerationModel, setIsAvatarGenerationModel] = useState(false);
    const [currentVoice, setCurrentVoice] = useState(null);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentPlayingId, setCurrentPlayingId] = useState(null);

    // Add event listener to audio element
    useEffect(() => {
        const audioElement = audioRef.current;
        if (audioElement) {
            const handlePlay = () => setIsPlaying(true);
            const handlePause = () => setIsPlaying(false);
            const handleEnded = () => {
                setIsPlaying(false);
                setCurrentPlayingId(null);
            };
            
            audioElement.addEventListener('play', handlePlay);
            audioElement.addEventListener('pause', handlePause);
            audioElement.addEventListener('ended', handleEnded);
            
            return () => {
                audioElement.removeEventListener('play', handlePlay);
                audioElement.removeEventListener('pause', handlePause);
                audioElement.removeEventListener('ended', handleEnded);
            };
        }
    }, [audioRef.current]);

    useEffect(() => {
        const _init = async () => {
            try {
                const myVoices = await getMyVoices();
                setMyVoices(myVoices);
            } catch (error) {
                console.log(error)
                setToast({
                    show: true,
                    message: '更新失敗，請稍後再試',
                    type: 'error'
                });
                throw error;
            }
        }
        const getApiKey = async () => {
            const result = await apiRequest('POST', '/api/index/getHeygenKey');
            if (result.success && result.data.value) {
                setHasApiKey(true)
            }
        }
        getApiKey();

        _init();

        // Listen for credit update events
        window.addEventListener('myVoicesUpdate', (e) => {
            if (e.detail && typeof e.detail === 'object') {
                setMyVoices(e.detail)
            }
        });

        window.addEventListener('showMessage', (e) => {
            if (e.detail && typeof e.detail === 'object') {
                setToast({
                    show: true,
                    message: e.detail.text || e.detail.message,
                    type: e.detail.type || 'info'
                });
            }
        })

        return () => {
            window.removeEventListener('myVoicesUpdate', () => {});
            window.removeEventListener('showMessage', () => {});
        };
    }, []);

    useEffect(() => {
        // Filter records based on search query and filter type
        const _filteredRecords = historyRecords.filter(record => {
            const matchesSearch = searchQuery 
                ? record.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  record.source.toLowerCase().includes(searchQuery.toLowerCase())
                : true;
                
            const isYoutube = record.source?.includes('youtube.com') || record.source?.includes('youtu.be');
            const recordType = isYoutube ? 'youtube' : 'pdf';
            
            const matchesFilter = filterType === 'all' || 
                (filterType === 'youtube' && recordType === 'youtube') ||
                (filterType === 'pdf' && recordType === 'pdf');
                
            return matchesSearch && matchesFilter;
        });
        setFilteredRecords(_filteredRecords);
    }, [historyRecords]);

    const handleShowGenerateModel = (voice) => {
        if (!hasApiKey) {
            setToast({
                show: true,
                message: '請先設定 HeyGen API Key',
                type: 'error'
            });
            
            return;
        }

        if (!voice) {
            setToast({
                show: true,
                message: '請先選擇一個語音記錄',
                type: 'error'
            });
            return;
        }

        setCurrentVoice(voice);
        setIsAvatarGenerationModel(true);
    }

    const handleGenerateAvatar = async (avatarId, videoSize, voice) => {

        if (!hasApiKey) {
            console.log("No API key, opening modal");
            alert("請先設定 HeyGen API Key");
            return;
        }

        if (!voice) {
            setToast({
                show: true,
                message: '請先選擇一個語音記錄',
                type: 'error'
            });
            return;
        }

        if (!avatarId) {
            setToast({
                show: true,
                message: '請輸入 Avatar ID',
                type: 'error'
            });
            return;
        }
        
        setIsGeneratingAvatar(true);
        setAvatarGenerationStatus('preparing');
        
        try {
            setAvatarGenerationStatus('uploading');
            
            // Send to webhook with binary data
            const result = await apiRequest('POST', '/api/index/generateVideo', {
                id: voice.id,
                avatarId,
                videoSize
            });

            if (result.data.credit) {
                dispatchCreditUpdateEvent(result.data.credit);
            }
            if (result.success) {
                setAvatarGenerationStatus('success');

                setToast({
                    show: true,
                    message: '語音資產已成功上傳至 HeyGen，您可以在 HeyGen 平台上使用此資產生成數字人影片',
                    type: 'success'
                });
            } else {
                throw new Error(result.error)
            }
            
        } catch (error) {
            setAvatarGenerationStatus('error');
            setToast({
                show: true,
                message: error.message || '上傳語音資產失敗',
                type: 'error'
            });
        } finally {
            setIsGeneratingAvatar(false);
            setAvatarGenerationStatus(null);
        }
    };

    try {

        useEffect(() => {
            if (!isAuthenticated()) {
                window.location.href = '/auth';
                return;
            }

            fetchHistory();
        }, []);

        const fetchHistory = async () => {
            setIsLoading(true);
            try {
                const historyData = await getHistory();
                setHistoryRecords(historyData);
            } catch (error) {
                console.error('Failed to fetch history:', error);
                setError('載入歷史記錄失敗，請稍後再試');
            } finally {
                setIsLoading(false);
            }
        };

        const handleClearHistory = async () => {
            if (!window.confirm('確定要清除所有轉換記錄嗎？此操作無法復原。')) {
                return;
            }
            
            try {
                setIsLoading(true);
                await clearHistory();
                setHistoryRecords([]);
                setToast({
                    show: true,
                    message: '所有記錄已成功清除',
                    type: 'success'
                });
            } catch (error) {
                console.error('Failed to clear history:', error);
                setToast({
                    show: true,
                    message: '清除記錄失敗',
                    type: 'error'
                });
            } finally {
                setIsLoading(false);
            }
        };

        const handleCopy = async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                setToast({
                    show: true,
                    message: '已複製到剪貼簿',
                    type: 'success'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            } catch (err) {
                console.error('Copy failed:', err);
                setToast({
                    show: true,
                    message: '複製失敗',
                    type: 'error'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            }
        };

        const toggleExpand = (id) => {
            setExpandedId(expandedId === id ? null : id);
        };

        const handleSearch = (e) => {
            setSearchQuery(e.target.value);
        };

        const handleFilterChange = (e) => {
            setFilterType(e.target.value);
        };

        const handleRecordSelect = (id) => {
            setSelectedRecordId(selectedRecordId === id ? null : id);
        };

        const handlePlayVoice = async (voice) => {
            if (!audioRef.current) return;
            
            if (currentPlayingId === voice.id) {
                // If this is already playing, pause it
                if (isPlaying) {
                    audioRef.current.pause();
                } else {
                    audioRef.current.play().catch(err => {
                        console.error('Failed to play audio:', err);
                        setToast({
                            show: true,
                            message: '播放失敗，請稍後再試',
                            type: 'error'
                        });
                    });
                }
            } else {
                // Play a different voice
                setCurrentPlayingId(voice.id);
                audioRef.current.src = voice.url;
                audioRef.current.load();
                audioRef.current.play().catch(err => {
                    console.error('Failed to play audio:', err);
                    setToast({
                        show: true,
                        message: '播放失敗，請稍後再試',
                        type: 'error'
                    });
                });
            }
        };

        const handleUpdateContent = async (recordId, newContent, newRewriteResults) => {
            try {
                const recordToUpdate = historyRecords.find(record => record.id === recordId);
                
                if (!recordToUpdate) {
                    throw new Error('找不到要更新的記錄');
                }

                const updatedRecord = {
                    ...recordToUpdate,
                    content: newContent,
                    ...(newRewriteResults && { rewrite_result: newRewriteResults })
                };
                
                setHistoryRecords(prevRecords => 
                    prevRecords.map(record => 
                        record.id === recordId ? updatedRecord : record
                    )
                );
                
                setToast({
                    show: true,
                    message: '更新成功',
                    type: 'success'
                });
            } catch (error) {
                console.error('Update error:', error);
                setToast({
                    show: true,
                    message: '更新失敗，請稍後再試',
                    type: 'error'
                });
                throw error;
            }
        };

        if (!isAuthenticated()) {
            return null; // Will redirect in useEffect
        }

        return (
            <div className="container mx-auto px-4 py-8" data-name="history-page">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4" data-name="page-title">轉換記錄</h1>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                            <div className="relative w-full sm:w-64">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearch}
                                    placeholder="搜尋記錄..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                                    data-name="search-input"
                                />
                                <i className="fas fa-search absolute right-3 top-3 text-gray-400"></i>
                            </div>
                            <select
                                value={filterType}
                                onChange={handleFilterChange}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 bg-white"
                                data-name="filter-select"
                            >
                                <option value="all">所有類型</option>
                                <option value="youtube">YouTube 轉文字</option>
                                <option value="pdf">PDF 轉 Excel</option>
                            </select>
                        </div>
                        <div className="flex gap-3 w-full md:w-auto">
                            <button
                                onClick={handleClearHistory}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex-1 md:flex-none"
                                disabled={isLoading || historyRecords.length === 0}
                                data-name="clear-history-button"
                            >
                                <i className="fas fa-trash-alt mr-2"></i>
                                清除所有記錄
                            </button>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64" data-name="loading">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded" data-name="error-message">
                        <div className="flex">
                            <i className="fas fa-exclamation-circle text-red-400 mr-3 mt-0.5"></i>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                ) : filteredRecords.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg border border-gray-200" data-name="empty-state">
                        <div className="text-6xl mb-4 text-gray-300">
                            <i className="fas fa-history"></i>
                        </div>
                        {historyRecords.length === 0 ? (
                            <div>
                                <h3 className="text-2xl font-bold mb-2">尚無轉換記錄</h3>
                                <p className="text-gray-600 mb-6">
                                    使用我們的工具轉換 YouTube 影片或 PDF 檔案，記錄將會顯示在這裡
                                </p>
                                <a 
                                    href="/workflow"
                                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
                                    data-name="workflow-link"
                                >
                                    <i className="fas fa-tools mr-2"></i>
                                    前往工作流工具
                                </a>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-2xl font-bold mb-2">找不到符合的記錄</h3>
                                <p className="text-gray-600 mb-4">
                                    嘗試調整搜尋條件或使用不同的過濾器
                                </p>
                                <button 
                                    onClick={() => {
                                        setSearchQuery('');
                                        setFilterType('all');
                                    }}
                                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                                    data-name="reset-filter-button"
                                >
                                    重設過濾條件
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4" data-name="history-list">
                        {filteredRecords.map(record => (
                            <HistoryRecord
                                hasApiKey={hasApiKey}
                                key={record.id}
                                record={record}
                                isExpanded={expandedId === record.id}
                                isSelected={selectedRecordId === record.id}
                                onToggle={() => toggleExpand(record.id)}
                                onSelect={() => handleRecordSelect(record.id)}
                                onCopy={handleCopy}
                                onUpdateContent={handleUpdateContent}
                                rewriteResult={record.id === selectedRecordId ? rewriteResult : null}
                                myVoices={myVoices}
                                showAvatarGenerateModel={handleShowGenerateModel}
                                currentPlayingId={currentPlayingId}
                                isPlaying={isPlaying}
                                onPlayVoice={handlePlayVoice}
                            />
                        ))}
                    </div>
                )}

                {/* Audio Player (hidden) */}
                <audio ref={audioRef} className="hidden" data-name="audio-player" />

                {/* Avatar Generation Modal */}
                {isAvatarGenerationModel && (
                    <AvatarGenerationModal 
                        isOpen={isAvatarGenerationModel}
                        onClose={() => {
                            setIsAvatarGenerationModel(false)
                            setCurrentVoice(null);
                        }}
                        onGenerate={handleGenerateAvatar}
                        isLoading={isGeneratingAvatar}
                        voice={currentVoice}
                    />
                )}

                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })}
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('History page error:', error);
        reportError(error);
        return null;
    }
}

export default History;